import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {NGXLogger} from 'ngx-logger';
import {SimpleDialogComponent} from '../../components/simple-dialog/simple-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {environment} from '../../../environments/environment';
import {AngularFireAnalytics} from '@angular/fire/compat/analytics';
import {Events} from '../../models/analytics';
import {MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltip, MatTooltipDefaultOptions} from '@angular/material/tooltip';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {ItemPickerComponent} from '../../components/item-picker/item-picker.component';
import {of} from 'rxjs';
import {LocalStorageService, STORAGE_KEYS} from '../../services/local-storage.service';
import {TurnstileComponent} from '../../components/turnstile/turnstile.component';
import {AuthService} from '../../services/auth.service';
import {classifyLoginError, LOGIN_FAILURE_DIALOG, LoginErrorReason} from './login-error';
import {SESSION_EXPIRED_REASON} from '../../models/session-expired-error';
import {sessionExpiredDialog} from './session-expired';
import {FunnelStateService} from '../../services/funnel-state.service';

export const myCustomTooltipDefaults: MatTooltipDefaultOptions = {
  showDelay: 0,
  hideDelay: 8000,
  touchendHideDelay: 8000,
};

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  providers: [
    {provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: myCustomTooltipDefaults}
  ],
})
export class LoginComponent implements OnInit {
  @ViewChild('reCaptcha', {static: false}) reCaptcha: TurnstileComponent;
  @ViewChild('reCaptcha', {static: false, read: ElementRef}) reCaptchaHost: ElementRef<HTMLElement>;
  startDate = new Date('1980-01-01');
  minDate = new Date();
  maxDate = new Date();
  submitting = false;
  challengeVisible = false;
  form: FormGroup;
  companyId: string;
  env = environment;

  hash: any;

  constructor(
    private router: Router,
    private authService: AuthService,
    private dialog: MatDialog,
    private logger: NGXLogger,
    private fb: FormBuilder,
    private analytics: AngularFireAnalytics,
    private localStorage: LocalStorageService,
    private funnelState: FunnelStateService
  ) {
    this.minDate.setFullYear(this.minDate.getFullYear() - 100);
    this.maxDate.setFullYear(this.maxDate.getFullYear() - 14);
  }

  // The overlay covers the whole page, so it must never be up while Cloudflare
  // is asking the visitor to solve a challenge.
  get loading(): boolean {
    return this.submitting && !this.challengeVisible;
  }

  ngOnInit(): void {
    const cred = this.localStorage.get(STORAGE_KEYS.user_credentials);
    this.companyId = cred?.company_id || '';
    this.form = this.fb.group({
      cpf: [cred?.cpf || '', Validators.required],
      birthday: [cred?.birthday || '', Validators.required],
      save_session: [cred?.save_session || false],
      recaptcha: ['']
    });
    this.explainSessionExpiry();
  }

  private explainSessionExpiry(): void {
    const state = history.state;
    if (state?.reason !== SESSION_EXPIRED_REASON) {
      return;
    }
    SimpleDialogComponent.open(this.dialog, sessionExpiredDialog());
    // Keeps `redirect` for after the login, but going Back/Forward to this entry
    // must not explain it again. (A reload starts from an empty state anyway.)
    history.replaceState({...state, reason: undefined}, '');
  }

  login(): void {
    // A second click while a challenge is on screen would raise the overlay
    // back over it, and execute() no-ops because one is already running: the
    // visitor would be locked out until the challenge times out.
    if (this.submitting) {
      return;
    }
    this.saveCredentials();
    // Before execute(): it can fail synchronously and clear the flag through
    // handleError, and setting it afterwards would leave a stuck spinner.
    this.submitting = true;
    this.reCaptcha.execute();
  }

  resolved(captchaResponse: string | null): void {
    this.challengeVisible = false;
    if (!!captchaResponse) {
      this.loginOrPickCompany(captchaResponse);
    } else {
      this.submitting = false;
    }
  }

  private loginOrPickCompany(captchaResponse: string): void {
    const values = this.form.value;

    this.authService.softLoginWithCpf(values.cpf, values.birthday, captchaResponse, this.companyId)
      .pipe(
        mergeMap(response => {
          // User has more than one company
          if (Array.isArray(response)) {
            // The submit is over; the picker drives the next one, and login()
            // refuses to start while this is still set.
            this.submitting = false;
            this.reCaptcha.reset();

            return ItemPickerComponent.open(this.dialog, {
              header: 'Selecione de qual empresa é o atestado',
              select: 'CONFIRMAR',
              itens: response
            }).afterClosed()
              .pipe(map(companyId => {
                this.companyId = companyId;

                return 'retry'; // try login again
              }));
          }

          this.companyId = response;

          return of('continue');
        }),
        catchError((error) => {
          this.submitting = false;
          this.reCaptcha.reset();
          this.logger.debug(error);
          const reason = classifyLoginError(error);
          this.reportLoginError(reason, {status: error?.status});

          return SimpleDialogComponent.open(this.dialog, LOGIN_FAILURE_DIALOG[reason])
            .afterClosed().pipe(map(() => 'stop'));
        })
      ).subscribe((proceed) => {
        if (proceed === 'continue') {
          this.funnelState.reset();
          const {redirect} = history.state;
          this.router.navigate([redirect || 's/'], {replaceUrl: true})
            .then(() => true)
            .finally(() => this.submitting = false);

        } else if (proceed === 'retry') {
          this.login();
        }
      });
  }

  private saveCredentials(): void {
    const values = this.form.value;
    if (values.save_session) {
      const saved = this.localStorage.get(STORAGE_KEYS.user_credentials) || {};
      // The saved contact belongs to the CPF that saved it, not to the next person on this device.
      if (saved.cpf !== values.cpf) {
        delete saved.phone;
        delete saved.email;
      }
      saved.cpf = values.cpf;
      saved.birthday = values.birthday;
      // saved.company_id = this.companyId,
      saved.save_session = values.save_session;
      this.localStorage.set(STORAGE_KEYS.user_credentials, saved);
    } else {
      this.localStorage.set(STORAGE_KEYS.user_credentials, {});
    }
  }

  private reportLoginError(reason: LoginErrorReason, extra: {status?: number; code?: string} = {}): void {
    this.analytics.logEvent(Events.LOGIN_ERROR, {reason, ...extra});
  }

  handleError(code: string): void {
    // A widget failure never reaches the backend, NGXLogger only writes to the
    // console (serverLogLevel is OFF) and Sentry only sees uncaught errors, so
    // without this event the failure leaves no trace anywhere at all.
    this.logger.error(`Captcha error: ${code}`);
    this.reportLoginError('captcha-widget', {code});
    this.challengeVisible = false;
    this.submitting = false;
    this.reCaptcha?.reset();
    SimpleDialogComponent.open(this.dialog, LOGIN_FAILURE_DIALOG.captcha);
  }

  onCaptchaInteractive(showing: boolean): void {
    this.challengeVisible = showing;
    if (showing) {
      // Visible is not the same as seen: on a short viewport the widget renders
      // below the fold and the visitor reads it as "nothing happened". Deferred
      // because before-interactive fires before Cloudflare expands the widget,
      // so centring it now would centre a box that has not grown yet.
      setTimeout(() => this.reCaptchaHost?.nativeElement?.scrollIntoView({
        block: 'center',
        behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth'
      }));
    }
  }

  onCaptchaUnavailable(code: string): void {
    // The Turnstile script failed to load at page open. Record it for telemetry
    // (script-load reliability) but do NOT alarm the user before they act — the
    // dialog only shows if they actually try to submit.
    this.logger.error(`Captcha unavailable: ${code}`);
    this.reportLoginError('captcha-unavailable', {code});
  }

  toggleTooltip(tooltip: MatTooltip, event): void {
    tooltip?.toggle();
    event.stopPropagation();
  }
}
